import { useEffect, useRef, useState } from 'react';

import { API_BASE_URL } from '../../config/api.js';
import './legacyI18n.js';

const LEGACY_RUNTIME_VERSION = '20260623-followup-editable-no-ai-search-v1';

function getLegacyAssetCandidates(filename) {
  const base = import.meta.env.BASE_URL || '/';
  const normalizedBase = base.endsWith('/') ? base : `${base}/`;
  return [...new Set([
    `${normalizedBase}${filename}`,
    filename,
    `/${filename}`
  ])];
}

async function fetchFirstAvailableAsset(filename) {
  const errors = [];
  for (const url of getLegacyAssetCandidates(filename)) {
    try {
      const response = await fetch(url, { cache: 'no-store' });
      if (response.ok) return response;
      errors.push(`${url}: ${response.status}`);
    } catch (error) {
      errors.push(`${url}: ${error.message}`);
    }
  }
  throw new Error(`Failed to load ${filename}. Tried ${errors.join(', ')}`);
}

function appendScriptOnce({ id, src }) {
  const existingScript = document.getElementById(id);
  if (existingScript) {
    return Promise.resolve(existingScript);
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = id;
    script.src = src;
    script.async = false;
    script.onload = () => resolve(script);
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.body.appendChild(script);
  });
}

async function appendScriptFromCandidates({ id, filename }) {
  const errors = [];
  for (const url of getLegacyAssetCandidates(filename)) {
    try {
      return await appendScriptOnce({ id, src: `${url}?v=${LEGACY_RUNTIME_VERSION}` });
    } catch (error) {
      const script = document.getElementById(id);
      script?.remove();
      errors.push(`${url}: ${error.message}`);
    }
  }
  throw new Error(`Failed to load ${filename}. Tried ${errors.join(', ')}`);
}

function openCustomerFormModal(event) {
  event?.preventDefault();
  event?.stopPropagation();

  const modal = document.getElementById('customer-form-modal');
  if (!modal) return;

  setModalFullscreen(modal, false);
  modal.classList.remove('hidden');
  modal.classList.add('active');
  window.showCustomerFormNotice?.(2000);
  window.prefillCustomerFormCompanyFields?.();
}

function syncFullscreenButtonStates(modal, isActive) {
  modal.querySelectorAll('.fullscreen-modal-btn').forEach((button) => {
    button.setAttribute('aria-pressed', String(isActive));
    button.classList.toggle('is-active', isActive);

    const enterIcon = button.querySelector('[data-icon="enter"]');
    const exitIcon = button.querySelector('[data-icon="exit"]');
    enterIcon?.classList.toggle('hidden', isActive);
    exitIcon?.classList.toggle('hidden', !isActive);
  });
}

function syncBodyFullscreenState() {
  const hasFullscreenModal = Boolean(document.querySelector('.modal.fullscreen.active'));
  document.body.classList.toggle('modal-fullscreen-active', hasFullscreenModal);
}

function resetModalDrag(modal) {
  const panel = modal.querySelector('.modal-panel');
  if (!panel) return;

  panel.dataset.dragX = '0';
  panel.dataset.dragY = '0';
  panel.style.transform = '';
}

function setModalFullscreen(modal, isActive) {
  modal.classList.toggle('fullscreen', isActive);
  syncFullscreenButtonStates(modal, isActive);
  syncBodyFullscreenState();

  if (isActive) {
    resetModalDrag(modal);
  }
}

function toggleFullscreenModal(event) {
  event.preventDefault();
  event.stopPropagation();
  event.nativeEvent?.stopImmediatePropagation?.();

  const button = event.currentTarget;
  const targetId = button.dataset.target || 'customer-form-modal';
  const modal = document.getElementById(targetId);
  if (!modal) return;

  setModalFullscreen(modal, !modal.classList.contains('fullscreen'));
}

function closeCustomerFormModal(event) {
  event.preventDefault();
  event.stopPropagation();

  const modal = document.getElementById('customer-form-modal');
  if (!modal) return;

  setModalFullscreen(modal, false);
  modal.classList.remove('active');
  window.hideCustomerFormNotice?.();
}

function installCustomerFormDrag() {
  const modal = document.getElementById('customer-form-modal');
  const panel = modal?.querySelector('.modal-panel');
  const handle = modal?.querySelector('.customer-form-hero');

  if (!modal || !panel || !handle || panel.dataset.dragBound === 'true') return;

  panel.dataset.dragBound = 'true';
  panel.dataset.dragX = '0';
  panel.dataset.dragY = '0';

  handle.addEventListener('pointerdown', (event) => {
    if (modal.classList.contains('fullscreen')) return;
    if (event.target.closest('button, a, input, select, textarea, [role="button"]')) return;

    const startX = event.clientX;
    const startY = event.clientY;
    const originX = Number(panel.dataset.dragX || 0);
    const originY = Number(panel.dataset.dragY || 0);

    handle.setPointerCapture?.(event.pointerId);
    handle.classList.add('is-dragging');

    const movePanel = (moveEvent) => {
      const nextX = originX + moveEvent.clientX - startX;
      const nextY = originY + moveEvent.clientY - startY;
      panel.dataset.dragX = String(nextX);
      panel.dataset.dragY = String(nextY);
      panel.style.transform = `translate3d(${nextX}px, ${nextY}px, 0)`;
    };

    const stopDrag = () => {
      handle.classList.remove('is-dragging');
      document.removeEventListener('pointermove', movePanel);
      document.removeEventListener('pointerup', stopDrag);
      document.removeEventListener('pointercancel', stopDrag);
    };

    document.addEventListener('pointermove', movePanel);
    document.addEventListener('pointerup', stopDrag, { once: true });
    document.addEventListener('pointercancel', stopDrag, { once: true });
  });
}

function ensureActiveChatForPi() {
  if (!window.state) return;

  const activeCustomer = window.state.customerData?.find(
    (customer) => String(customer.id) === String(window.state.activeChatId)
  );
  if (activeCustomer) return;

  const activeChatItem = document.querySelector('.chat-list-item.active[data-customer-id]');
  const customerId = activeChatItem?.dataset.customerId;
  if (customerId) {
    const matchedCustomer = window.state.customerData?.find(
      (customer) => String(customer.id) === String(customerId)
    );
    if (matchedCustomer) {
      window.state.activeChatId = matchedCustomer.id;
    }
  }
}

function openPiModalFromButton(event) {
  const target = event.target instanceof Element ? event.target : null;
  const trigger = target?.closest('#create-pi-btn, #quick-pi-btn');
  if (!trigger) return;

  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation?.();

  ensureActiveChatForPi();

  if (trigger.id === 'quick-pi-btn' && typeof window.openChatSmartPI === 'function') {
    window.openChatSmartPI(trigger.dataset.customerId || window.state?.activeChatId, {
      waAccountId: trigger.dataset.waAccountId || window.state?.activeWaAccountId
    });
    return;
  }

  if (typeof window.openPIModal === 'function') {
    window.openPIModal();
  } else {
    console.error('PI modal runtime is not ready yet.');
  }
}


const AGENT_ALERT_RULE_TYPES = [
  { type: 'pi_opened', label: '首次打开 PI', description: '客户第一次打开可追踪 PI 时提醒。', defaultOpenCount: 1, cooldownMinutes: 60, maxPerDay: 20 },
  { type: 'pi_repeated_open', label: '重复查看 PI', description: '客户多次打开同一份 PI，通常代表正在评估报价。', defaultOpenCount: 3, cooldownMinutes: 120, maxPerDay: 10 },
  { type: 'pi_hot_lead', label: '高意向客户', description: '客户高频查看 PI 但未付款，需要当天重点跟进。', defaultOpenCount: 5, cooldownMinutes: 180, maxPerDay: 8 },
  { type: 'payment_signal_detected', label: '付款信号', description: '识别到客户进入付款或定金推进阶段。', defaultOpenCount: null, cooldownMinutes: 120, maxPerDay: 10 },
  { type: 'quote_signal_detected', label: '报价信号', description: '识别到客户集中询价、议价或要求更新报价。', defaultOpenCount: null, cooldownMinutes: 120, maxPerDay: 10 }
];

function escapeLegacyText(value = '') {
  return String(value).replace(/[&<>'"]/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[char]));
}

function ensureAgentAlertSettingsModalFallback() {
  let modal = document.getElementById('agent-alert-settings-modal');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'agent-alert-settings-modal';
    modal.className = 'resend-settings-modal hidden';
    modal.innerHTML = '<div class="resend-settings-backdrop" data-resend-close></div><div class="resend-settings-dialog" role="dialog" aria-modal="true" aria-label="邮件推送设置"><div id="agent-alert-settings-body"></div></div>';
    document.body.appendChild(modal);
  }
  return modal;
}

function renderAgentAlertSettingsFallback(mode = 'ready', data = {}) {
  const body = document.getElementById('agent-alert-settings-body');
  if (!body) return;
  const resend = data.resend || {};
  const rules = Array.isArray(data.rules) ? data.rules : [];
  const email = data.email || rules.map((rule) => rule.emailTo || rule.email_to || '').find(Boolean) || resend.defaultToEmail || localStorage.getItem('geekllyResendAlertEmail') || '';

  if (mode === 'loading') {
    body.innerHTML = `
      <div class="resend-settings-head"><div><p>RESEND ALERTS</p><h3>邮件推送设置</h3><span>正在检测 Worker 与 Resend 连接状态...</span></div><button type="button" data-resend-close>×</button></div>
      <div class="resend-settings-loading"><div class="loader"></div><span>正在读取提醒规则...</span></div>`;
    return;
  }

  if (mode === 'error') {
    body.innerHTML = `
      <div class="resend-settings-head"><div><p>RESEND ALERTS</p><h3>邮件推送设置</h3><span>用于配置 PI 行为提醒接收邮箱。</span></div><button type="button" data-resend-close>×</button></div>
      <div class="resend-settings-error"><strong>设置读取失败</strong><p>${escapeLegacyText(data.message || '请确认已经登录系统，并检查 Worker 接口。')}</p><button type="button" data-resend-retry>重新检测</button></div>`;
    return;
  }

  const ruleRows = AGENT_ALERT_RULE_TYPES.map((config) => {
    const rule = rules.find((item) => item.ruleType === config.type || item.rule_type === config.type) || {};
    const enabled = rule.enabled !== false;
    const openCount = Number(rule.threshold?.openCount ?? rule.threshold_json?.openCount ?? config.defaultOpenCount ?? 0);
    return `
      <label class="resend-rule-row">
        <input type="checkbox" data-alert-rule-toggle="${config.type}" ${enabled ? 'checked' : ''}>
        <span class="resend-rule-main"><strong>${config.label}</strong><small>${config.description}</small></span>
        ${config.defaultOpenCount ? `<span class="resend-rule-threshold">打开 <input type="number" min="1" max="99" value="${openCount || config.defaultOpenCount}" data-alert-rule-threshold="${config.type}"> 次</span>` : '<span class="resend-rule-threshold is-muted">AI信号</span>'}
      </label>`;
  }).join('');

  body.innerHTML = `
    <div class="resend-settings-head"><div><p>RESEND ALERTS</p><h3>邮件推送设置</h3><span>配置 PI 打开、高意向客户和成交信号的接收邮箱。</span></div><button type="button" data-resend-close>×</button></div>
    <div class="resend-settings-status-card"><div><strong>Resend 连接状态</strong><p>${resend.fromEmail ? `发件邮箱：${escapeLegacyText(resend.fromEmail)}` : '发件邮箱使用 Worker 默认配置'}</p></div><span class="resend-status-pill ${resend.connected ? 'is-connected' : 'is-disconnected'}"><span></span>${resend.connected ? 'RESEND 已连接' : 'RESEND 未连接'}</span></div>
    <label class="resend-email-field"><span>提醒接收邮箱</span><input id="resend-alert-email" type="email" value="${escapeLegacyText(email)}" placeholder="例如 boss@company.com"><small>保存后，PI 雷达触发的高意向提醒会发送到这个邮箱。</small></label>
    <div class="resend-rules-list">${ruleRows}</div>
    <div id="resend-settings-feedback" class="resend-settings-feedback ${data.message ? '' : 'hidden'}">${escapeLegacyText(data.message || '')}</div>
    <div class="resend-settings-actions"><button type="button" class="resend-secondary-btn" data-resend-test>发送测试邮件</button><button type="button" class="resend-primary-btn" data-resend-save>保存提醒设置</button></div>`;
}

async function loadAgentAlertSettingsFallback() {
  let healthConnected = false;
  try {
    const healthRes = await fetch(`${API_BASE_URL.replace(/\/$/, '')}/health`, { cache: 'no-store', credentials: 'include' });
    const health = await healthRes.json().catch(() => ({}));
    healthConnected = Boolean(health.bindings?.RESEND_API_KEY);
  } catch (error) {}

  const response = await fetch(`${API_BASE_URL.replace(/\/$/, '')}/api/alerts/rules?t=${Date.now()}`, { cache: 'no-store', credentials: 'include' });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload.error || payload.message || `提醒设置接口请求失败 (${response.status})`);
  return {
    rules: Array.isArray(payload.rules) ? payload.rules : [],
    defaults: Array.isArray(payload.defaults) ? payload.defaults : [],
    resend: { ...(payload.resend || {}), connected: Boolean(payload.resend?.connected || healthConnected) }
  };
}

function installAgentAlertSettingsFallback() {
  if (typeof window.openAgentAlertSettings !== 'function') {
    window.openAgentAlertSettings = async function openAgentAlertSettingsFallback() {
      const modal = ensureAgentAlertSettingsModalFallback();
      modal.classList.remove('hidden');
      renderAgentAlertSettingsFallback('loading');
      try {
        const data = await loadAgentAlertSettingsFallback();
        window.__GEEKLLY_RESEND_SETTINGS__ = data;
        renderAgentAlertSettingsFallback('ready', data);
      } catch (error) {
        renderAgentAlertSettingsFallback('error', { message: error.message || '提醒设置读取失败' });
      }
    };
  }

  window.closeAgentAlertSettings = window.closeAgentAlertSettings || function closeAgentAlertSettingsFallback() {
    document.getElementById('agent-alert-settings-modal')?.classList.add('hidden');
  };

  window.saveAgentAlertSettings = window.saveAgentAlertSettings || async function saveAgentAlertSettingsFallback() {
    const email = String(document.getElementById('resend-alert-email')?.value || '').trim();
    const feedback = document.getElementById('resend-settings-feedback');
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      if (feedback) { feedback.classList.remove('hidden'); feedback.textContent = '请输入有效的接收邮箱'; }
      return;
    }
    try {
      const requests = AGENT_ALERT_RULE_TYPES.map((config) => {
        const enabled = document.querySelector(`[data-alert-rule-toggle="${config.type}"]`)?.checked !== false;
        const thresholdInput = document.querySelector(`[data-alert-rule-threshold="${config.type}"]`);
        const openCount = Math.max(1, Number(thresholdInput?.value || config.defaultOpenCount || 1));
        return fetch(`${API_BASE_URL.replace(/\/$/, '')}/api/alerts/rules`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: `default-${config.type}`,
            ruleType: config.type,
            enabled,
            threshold: config.defaultOpenCount ? { openCount } : {},
            channels: { email: true, dashboard: true },
            emailTo: email,
            cooldownMinutes: config.cooldownMinutes,
            maxPerDay: config.maxPerDay
          })
        }).then(async (res) => {
          const payload = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(payload.error || payload.message || `${config.label} 保存失败`);
          return payload.rule;
        });
      });
      const savedRules = await Promise.all(requests);
      localStorage.setItem('geekllyResendAlertEmail', email);
      const nextData = { ...(window.__GEEKLLY_RESEND_SETTINGS__ || {}), rules: savedRules.filter(Boolean) };
      window.__GEEKLLY_RESEND_SETTINGS__ = nextData;
      renderAgentAlertSettingsFallback('ready', { ...nextData, email, message: '提醒设置已保存。' });
    } catch (error) {
      if (feedback) { feedback.classList.remove('hidden'); feedback.textContent = error.message || '保存失败'; }
    }
  };

  window.sendAgentAlertTestEmail = window.sendAgentAlertTestEmail || async function sendAgentAlertTestEmailFallback() {
    const email = String(document.getElementById('resend-alert-email')?.value || '').trim();
    const feedback = document.getElementById('resend-settings-feedback');
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      if (feedback) { feedback.classList.remove('hidden'); feedback.textContent = '请先填写有效邮箱'; }
      return;
    }
    try {
      if (feedback) { feedback.classList.remove('hidden'); feedback.textContent = '正在发送测试邮件...'; }
      const response = await fetch(`${API_BASE_URL.replace(/\/$/, '')}/api/alerts/test-email`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emailTo: email })
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || payload.message || '测试邮件发送失败');
      if (feedback) feedback.textContent = payload.status === 'sent' ? '测试邮件已发送。' : `测试邮件状态：${payload.status || '已提交'}`;
    } catch (error) {
      if (feedback) feedback.textContent = error.message || '测试邮件发送失败';
    }
  };

  if (!window.__GEEKLLY_RESEND_CLICK_DELEGATE_BOUND__) {
    window.__GEEKLLY_RESEND_CLICK_DELEGATE_BOUND__ = true;
    document.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest('[data-resend-close]')) {
        event.preventDefault();
        window.closeAgentAlertSettings?.();
        return;
      }
      if (target?.closest('[data-resend-retry]')) {
        event.preventDefault();
        window.openAgentAlertSettings?.();
        return;
      }
      if (target?.closest('[data-resend-save]')) {
        event.preventDefault();
        window.saveAgentAlertSettings?.();
        return;
      }
      if (target?.closest('[data-resend-test]')) {
        event.preventDefault();
        window.sendAgentAlertTestEmail?.();
        return;
      }
      const button = target?.closest('button');
      if (['RESEND设置', '邮件推送'].includes(button?.textContent?.trim())) {
        event.preventDefault();
        event.stopPropagation();
        window.openAgentAlertSettings?.();
      }
    }, true);
  }
}

function bindLegacyEntryPoints() {
  installAgentAlertSettingsFallback();
  const customerFormBtn = document.getElementById('customer-form-btn');
  if (customerFormBtn && customerFormBtn.dataset.reactLegacyBound !== 'true') {
    customerFormBtn.dataset.reactLegacyBound = 'true';
    customerFormBtn.addEventListener('click', openCustomerFormModal);
  }

  installCustomerFormDrag();

  document.querySelectorAll('.fullscreen-modal-btn').forEach((button) => {
    if (button.dataset.reactLegacyFullscreenBound === 'true') return;
    button.dataset.reactLegacyFullscreenBound = 'true';
    button.addEventListener('click', toggleFullscreenModal);
  });

  document.querySelectorAll('#customer-form-modal .cancel-modal-btn').forEach((button) => {
    if (button.dataset.reactLegacyCloseBound === 'true') return;
    button.dataset.reactLegacyCloseBound = 'true';
    button.addEventListener('click', closeCustomerFormModal);
  });

  if (!window.__GEEKLLY_PI_CLICK_DELEGATE_BOUND__) {
    window.__GEEKLLY_PI_CLICK_DELEGATE_BOUND__ = true;
    document.addEventListener('click', openPiModalFromButton, true);
  }

  const piButtons = ['create-pi-btn', 'quick-pi-btn']
    .map((id) => document.getElementById(id))
    .filter(Boolean);

  piButtons.forEach((button) => {
    if (button.dataset.reactLegacyPiBound === 'true') return;

    button.dataset.reactLegacyPiBound = 'true';
    button.onclick = null;
    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      ensureActiveChatForPi();

      if (button.id === 'quick-pi-btn' && typeof window.openChatSmartPI === 'function') {
        window.openChatSmartPI(button.dataset.customerId || window.state?.activeChatId, {
          waAccountId: button.dataset.waAccountId || window.state?.activeWaAccountId
        });
        return;
      }

      if (typeof window.openPIModal === 'function') {
        window.openPIModal();
      } else {
        console.error('PI modal runtime is not ready yet.');
      }
    });
  });
}

function installLoginSubmitGuard() {
  const loginForm = document.getElementById('login-form');
  if (!loginForm || loginForm.dataset.reactLegacySubmitGuard === 'true') return;

  loginForm.dataset.reactLegacySubmitGuard = 'true';
  loginForm.addEventListener('submit', (event) => {
    event.preventDefault();
  }, true);
}

export function LegacyApp() {
  const hostRef = useRef(null);
  const [bootError, setBootError] = useState(null);
  const [legacyMarkup, setLegacyMarkup] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function loadLegacyMarkup() {
      try {
        const response = await fetchFirstAvailableAsset('legacy-markup.html');
        const markup = await response.text();
        if (!cancelled) setLegacyMarkup(markup);
      } catch (error) {
        if (!cancelled) setBootError(error);
      }
    }

    loadLegacyMarkup();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!legacyMarkup) return undefined;

    let cancelled = false;

    async function bootLegacyRuntime() {
      window.GEEKLLY_API_BASE_URL = API_BASE_URL;
      window.getLegacyApiBaseUrl = window.getLegacyApiBaseUrl || (() => API_BASE_URL.replace(/\/$/, ''));
      installLoginSubmitGuard();
      installAgentAlertSettingsFallback();

      if (window.__GEEKLLY_LEGACY_BOOTSTRAPPED__) {
        bindLegacyEntryPoints();
        return;
      }

      try {
        await appendScriptFromCandidates({
          id: 'geeklly-legacy-runtime',
          filename: 'legacy-runtime.js'
        });

        if (cancelled) return;

        window.__GEEKLLY_LEGACY_BOOTSTRAPPED__ = true;
        document.dispatchEvent(new Event('DOMContentLoaded', { bubbles: true, cancelable: true }));
        window.dispatchEvent(new Event('load'));
        bindLegacyEntryPoints();
        window.setTimeout(bindLegacyEntryPoints, 0);
      } catch (error) {
        if (!cancelled) setBootError(error);
      }
    }

    bootLegacyRuntime();

    return () => {
      cancelled = true;
    };
  }, [legacyMarkup]);

  return (
    <>
      {bootError ? (
        <div className="min-h-screen bg-slate-950 p-6 text-white">
          <div className="mx-auto max-w-xl rounded-lg border border-red-400/30 bg-red-500/10 p-5">
            <h1 className="text-lg font-semibold">应用加载失败</h1>
            <p className="mt-2 text-sm text-red-100">{bootError.message}</p>
          </div>
        </div>
      ) : null}
      <div ref={hostRef} dangerouslySetInnerHTML={{ __html: legacyMarkup }} />
    </>
  );
}
